import React from 'react';
import type { DashboardFilters } from '../../types';

interface Props {
  filters: DashboardFilters;
  onFiltersChange: (f: DashboardFilters) => void;
  onClose: () => void;
  emptyFilters: DashboardFilters;
}

export default function FilterSidebar({ filters, onFiltersChange, onClose, emptyFilters }: Props) {
  const set = (field: keyof DashboardFilters, value: string | undefined) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const hasActiveFilters =
    filters.locationText ||
    filters.inspectorText ||
    filters.lastInspectedFrom ||
    filters.nextDueBefore;

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-40 flex justify-end"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Sidebar panel */}
      <div className="relative z-50 bg-white w-80 max-w-full h-full shadow-2xl flex flex-col slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-bold text-[#4C9E00] flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Advanced Filters
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filter fields */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {/* Location text search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Location Search 🔗
            </label>
            <input
              type="text"
              value={filters.locationText}
              onChange={(e) => set('locationText', e.target.value)}
              placeholder="e.g. North, Shaft…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Inspector text search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Inspector Search 🔗
            </label>
            <input
              type="text"
              value={filters.inspectorText}
              onChange={(e) => set('inspectorText', e.target.value)}
              placeholder="e.g. Smith…"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Last inspected date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Last Inspected On or After
            </label>
            <input
              type="date"
              value={filters.lastInspectedFrom ?? ''}
              onChange={(e) =>
                set('lastInspectedFrom', e.target.value || undefined)
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            {filters.lastInspectedFrom && (
              <p className="text-xs text-[#4C9E00] mt-1">🔗 Filter active</p>
            )}
          </div>

          {/* Next due before */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Next Inspection Due Before
            </label>
            <input
              type="date"
              value={filters.nextDueBefore ?? ''}
              onChange={(e) =>
                set('nextDueBefore', e.target.value || undefined)
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            {filters.nextDueBefore && (
              <p className="text-xs text-[#4C9E00] mt-1">🔗 Filter active</p>
            )}
          </div>

          {/* Active filter summary */}
          {hasActiveFilters && (
            <div className="bg-[#EBF7E0] border border-[#4C9E00]/20 rounded-lg p-3 text-sm text-[#4C9E00]">
              Active text/date filters applied. Grid is filtered.
            </div>
          )}
        </div>

        {/* Reset footer */}
        <div className="px-5 py-4 border-t border-gray-200">
          <button
            onClick={() => onFiltersChange(emptyFilters)}
            className="w-full border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition"
          >
            Reset All Filters
          </button>
        </div>
      </div>
    </div>
  );
}
